import React from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { themeColor } from '../Theming';
import {
  navigateToAndroidSettings,
  onClose,
} from '../Common/KindredAndroidInterface/KindredAndroidInterface';
import {
  ANALYTICS_PAGE_NAMES,
  trackClickedActionEvent,
} from '../Common/Analytics/utils';
import { usePageViewTracking, useQuery } from '../Common/Hooks';
import { normalizeText } from '../EonPluginActivation/utils';
import Header from './components/Header';
import ActivateNow from './components/ActivateNow';
import ActivationSteps from './components/ActivationSteps';
import { BoldText } from './BlueLightCardPluginActivationAlwaysAllow';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  max-height: 100dvh;
  width: 100vw;
  max-width: 600px;
  margin: 0 auto;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
`;

const BottomContent = styled.section`
  flex-direction: column;
  padding-bottom: 0px;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding-bottom: 140px;
`;

const ButtonsContainer = styled.div`
  padding-top: 10px;
  padding-inline: 30px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 600px;
  z-index: 2;
  background-color: ${themeColor('white')};

  & > button {
    background-color: ${themeColor('blueLightCardPrimary')};
    width: 336px;
    height: 48px;
    margin-top: 10px;
    border-radius: 4px;
    opacity: 1;
    gap: 8px;
    padding: 8px 24px;
    color: ${themeColor('white')};
    font-size: 16px;
    font-weight: 700;
    font-family: 'Museo Sans', sans-serif;
    border: none;

    &:last-child {
      background-color: ${themeColor('white')};
      color: ${themeColor('blueLightCardPrimary')};
      border: none;
    }
  }
`;

const DisclaimerContainer = styled.div`
  background-color: ${themeColor('lightGreyBackground')};
  margin-inline: 25px;
  margin-top: 15px;
  margin-bottom: 15px;
  border-radius: 10px;
  padding: 10px;
  overflow-y: auto;
  min-height: 206px;
  max-height: 30vh;
  font-family: arial;
  color: ${themeColor('black')};
  font-weight: normal;
  box-shadow: 0px 0px 8px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
`;

const DisclaimerParagraph = styled.p`
  font-family: Arial;
  color: ${themeColor('black')};
  font-weight: 400;
  margin: 0;
  padding-bottom: 5px;
  font-size: 15px;
  line-height: 21px;
  letter-spacing: 0px;

  & > a {
    color: ${themeColor('black')};
    text-decoration: underline;
`;

const DisclaimerHeading = styled.p`
  font-family: Arial;
  color: ${themeColor('black')};
  font-weight: 400;
  margin: 0 0 5px 0;
  padding: 0;
  font-size: 15px;
  line-height: 21px;
  letter-spacing: 0px;
`;

const BlueLightCardPluginActivationAndroidAccessibility: React.FC = () => {
  const { t } = useTranslation();
  const query = useQuery();
  const { accessibility_instructions_page } = ANALYTICS_PAGE_NAMES;
  usePageViewTracking(accessibility_instructions_page, 'BlueLightCard');

  const renderContent = () => {
    return (
      <Container>
        <Header
          height={170}
          starTopPosition={'90%'}
          androidAccessibility={true}
          heading={
            <BoldText>
              It’s easy to activate the Blue Light Card Deal Finder
            </BoldText>
          }
        />
        <ScrollableContent>
          <BottomContent>
            <ActivationSteps
              title={'Follow these easy steps:'}
              steps={[
                "Tap <strong>'Agree & continue'</strong> to open <strong>'Settings'</strong>",
                "Tap <strong>'Installed apps'</strong> to find <strong>'Blue Light Card'</strong>",
                "Toggle <strong>'Blue Light Card'</strong> on to activate",
              ]}
            />
          </BottomContent>
          <DisclaimerContainer tabIndex={0}>
            <DisclaimerParagraph>
              By enabling the AccessibilityService API, you grant permission for
              our Blue Light Card Deal Finder to access information about the
              installed apps on your device. We use this specifically to detect
              when you&apos;re using a browser app (e.g. Chrome etc.) so that we
              can show discount pop-ups only when you&apos;re shopping online in
              the browser.
            </DisclaimerParagraph>

            <DisclaimerHeading>
              Here&apos;s what we use the AccessibilityService permission for:
            </DisclaimerHeading>

            <DisclaimerParagraph>
              To access a list of installed and active apps, so we can detect
              when you&apos;re using a browser app for online shopping.
            </DisclaimerParagraph>

            <DisclaimerParagraph>
              To access your web browsing history for showing relevant offers.
            </DisclaimerParagraph>

            <DisclaimerParagraph>
              To create an anonymised click ID (using device or other
              identifiers) for linking purchases with shown offers.
            </DisclaimerParagraph>

            <DisclaimerParagraph>
              We also share your device location and the domains of websites you
              visit with a third party, where applicable, in order to retrieve
              and display valid discounts and offers as part of the Blue Light
              Card Deal Finder.
            </DisclaimerParagraph>
          </DisclaimerContainer>
          <ButtonsContainer>
            <button
              id="android-accessiblity-button-agree"
              onClick={() => {
                navigateToAndroidSettings();
                trackClickedActionEvent(
                  query,
                  'BlueLightCard',
                  'agree',
                  accessibility_instructions_page
                );
              }}
              aria-label="Agree to Accessibility permissions"
            >
              Agree & continue
            </button>
            <button
              onClick={() => {
                onClose?.();
                trackClickedActionEvent?.(
                  query,
                  'BlueLightCard',
                  'disagree',
                  accessibility_instructions_page
                );
              }}
              id="android-accessiblity-button-disagree"
              aria-label="Disagree to Accessibility permissions"
            >
              Disagree
            </button>
          </ButtonsContainer>
        </ScrollableContent>
      </Container>
    );
  };

  return renderContent();
};

export default BlueLightCardPluginActivationAndroidAccessibility;
